/**
 * DEC-1148 (FEEDBACK-4): the Feedback FAB + panel.
 *
 * Mounted app-shell-wide on authenticated surfaces, consumer AND operator, and
 * ALSO from the DEC-1146 ErrorBoundary fallback — the moment a user most needs
 * to report something is the moment the app has just blanked, and every other
 * reporting surface has been unmounted with it.
 *
 * ── Kill switch polarity ─────────────────────────────────────────────────
 * Gated on a `feedback_widget_disabled` flag, NOT `feedback_widget_enabled`.
 * `useFeatureFlag` returns false for an undefined flag, so an "enabled" flag
 * would hide the widget everywhere until someone remembered to create the
 * flag row — a silent failure that looks exactly like the feature not
 * shipping. A kill switch should require an explicit act to kill.
 */

import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import FeatureFlagsContext from '../../context/FeatureFlagsContext';
import { useModalFocusTrap } from '../../hooks/useModalFocusTrap';
import { withTransientRetry, isTransientRequestError } from '../../utils/transientRetry';
import { trackClientEvent } from '../../services/analytics';
import { getRecentErrors } from '../../services/errorBuffer';
import { APP_VERSION } from '../../utils/appVersion';
import { feedbackAPI } from '../../services/api';
import FeedbackForm from './FeedbackForm';
import {
  loadDraft, saveDraft, clearDraft, isRestorable, pageLabelFor, currentPath,
} from './feedbackDraft';

const EMPTY = { body: '', expected: '', category: null, severity: null, attachTechnical: true };

const uuid = () => (
  typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    // Older Safari. Only used as a funnel join key, never as a credential.
    : 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = (Math.random() * 16) | 0;
      return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
    })
);

// A 429 is NOT retried here, even though the shared helper treats it as
// transient. That default is right for a dashboard data feed, and wrong for a
// user-initiated submission: the limiter is telling us this user has already
// sent several reports, so retrying twice more inside the same window burns
// their remaining budget AND delays showing them the "please wait" message.
// Everything else transient (network drop, 5xx) still retries.
const isRetryableSubmit = (err) => isTransientRequestError(err) && err?.response?.status !== 429;

// `retryDelays` is a TEST SEAM: tests pass [] so a failure path resolves without
// real timers. Production always uses the shared schedule.
const FeedbackWidget = ({ isMobile = false, userEmail = null, retryDelays = undefined }) => {
  // Read the flag WITHOUT useFeatureFlag(), which throws when no provider is
  // above it. This component also renders from the ErrorBoundary fallback, and
  // a fallback that throws is worse than no fallback — that's the whole
  // principle AppErrorBoundary is built on. Today the boundary does sit inside
  // FeatureFlagsProvider, but moving it further out (to catch provider errors
  // themselves) is a reasonable future change that must not silently blank the
  // crash screen. No provider → not killed → the widget still renders, which
  // is the correct default for a kill switch on a crash surface.
  const flagsCtx = useContext(FeatureFlagsContext);
  const killed = !!flagsCtx?.flags?.feedback_widget_disabled;

  const [open, setOpen] = useState(false);
  const [fields, setFields] = useState(EMPTY);
  const [restoredDraft, setRestoredDraft] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [sent, setSent] = useState(false);
  const [copyFallback, setCopyFallback] = useState(null);

  const sessionIdRef = useRef(null);
  const typedRef = useRef(false);
  const dialogRef = useRef(null);

  const trap = useModalFocusTrap({
    active: open,
    containerRef: dialogRef,
    onClose: () => setOpen(false),
    // Escape must not abandon an in-flight submit — the request may already
    // have created the row, and closing would tell the user nothing happened.
    closeGuard: () => submitting,
  });

  const openPanel = useCallback(() => {
    sessionIdRef.current = uuid();
    typedRef.current = false;
    setError(null);
    setSent(false);
    setCopyFallback(null);

    const draft = loadDraft();
    if (isRestorable(draft)) {
      setFields({ ...EMPTY, ...draft });
      setRestoredDraft(true);
    } else {
      setFields(EMPTY);
      setRestoredDraft(false);
    }
    setOpen(true);
    trackClientEvent('bug_widget_opened', {
      bug_widget_session_id: sessionIdRef.current,
      route: currentPath(),
    });
  }, []);

  const change = (patch) => {
    setFields((f) => {
      const next = { ...f, ...patch };
      saveDraft(next);
      return next;
    });
    // Fires once per open. The funnel question is "did they start writing",
    // not "how many keystrokes".
    if (!typedRef.current && (patch.body || patch.expected)) {
      typedRef.current = true;
      trackClientEvent('bug_widget_started_typing', { bug_widget_session_id: sessionIdRef.current });
    }
  };

  const discardDraft = () => {
    clearDraft();
    setFields(EMPTY);
    setRestoredDraft(false);
  };

  const buildPayload = () => ({
    body: fields.expected.trim()
      ? `${fields.body.trim()}\n\nExpected instead: ${fields.expected.trim()}`
      : fields.body.trim(),
    category: fields.category || undefined,
    severity: fields.severity || undefined,
    route: currentPath(),
    appVersion: APP_VERSION,
    // The opt-out is real: unchecking it sends nothing captured, not a
    // filtered subset. Anything less would make the checkbox a lie.
    errors: fields.attachTechnical ? getRecentErrors().slice(-10) : [],
    context: fields.attachTechnical
      ? { viewport: `${window.innerWidth}x${window.innerHeight}`, userAgent: navigator.userAgent }
      : {},
    widgetSessionId: sessionIdRef.current,
  });

  const submit = async () => {
    setSubmitting(true);
    setError(null);
    setCopyFallback(null);
    const payload = buildPayload();
    try {
      await withTransientRetry(() => feedbackAPI.submit(payload), {
        isTransient: isRetryableSubmit,
        ...(retryDelays ? { delays: retryDelays } : {}),
      });
      clearDraft();
      setSent(true);
      trackClientEvent('bug_widget_confirmed', {
        bug_widget_session_id: sessionIdRef.current,
        category: payload.category || null,
        severity: payload.severity || null,
      });
    } catch (err) {
      const status = err?.response?.status;
      if (status === 429) {
        // Draft deliberately KEPT — they'll want to send this shortly.
        setError(err?.response?.data?.error || 'You’ve sent us a few reports just now. Please wait a few minutes and try again.');
      } else if (status >= 400 && status < 500) {
        setError(err?.response?.data?.error || 'We couldn’t send that. Please check what you wrote and try again.');
      } else {
        // Offline or 5xx after retries. Their words are the thing we must not
        // lose — offer the clipboard so the report survives even if we don't.
        setError('We couldn’t reach DECO just now. Your draft is saved — try again, or copy the report and email it to us.');
        setCopyFallback(payload);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const copyReport = async () => {
    const text = [
      'DECO bug report',
      `Page: ${copyFallback.route}`,
      `Version: ${copyFallback.appVersion}`,
      copyFallback.category ? `Type: ${copyFallback.category}` : null,
      copyFallback.severity ? `Severity: ${copyFallback.severity}` : null,
      '',
      copyFallback.body,
    ].filter(Boolean).join('\n');
    try {
      await navigator.clipboard.writeText(text);
      setError('Copied. Please email it to deco@decoenergy.net.');
      setCopyFallback(null);
    } catch {
      setError('Couldn’t copy automatically — please select your text above and copy it manually.');
    }
  };

  // Close on route change: a panel that follows the user to another page is
  // reporting the wrong page by the time they submit.
  useEffect(() => {
    if (!open) return undefined;
    const onPop = () => setOpen(false);
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, [open]);

  if (killed) return null;

  const fab = (
    <button
      type="button"
      onClick={openPanel}
      aria-haspopup="dialog"
      aria-expanded={open}
      aria-label="Beta Feedback"
      title="Beta Feedback"
      style={{
        position: 'fixed',
        right: '24px',
        // Mobile clears the bottom tab bar plus the device's safe area; the
        // widget must never sit under a nav the user can't move.
        bottom: isMobile ? 'calc(60px + env(safe-area-inset-bottom) + 16px)' : '24px',
        // Below modals (which sit at 1000+) so it never overlays a dialog.
        zIndex: 900,
        // Founder direction 2026-08-09: the pill became a blue CIRCLE holding
        // an outline-only chat bubble with "BETA" inside it in DECO lettering.
        // The visible name moved into the icon, so the accessible name lives
        // on aria-label/title above.
        width: '64px',
        height: '64px',
        padding: 0,
        borderRadius: '50%',
        border: 0,
        background: 'var(--navy)',
        color: '#fff',
        cursor: 'pointer',
        boxShadow: '0 2px 12px rgba(0,0,0,0.18)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {/* Chat bubble: OUTLINE only (stroke, no fill), tail bottom-left.
          "BETA" follows the wordmark treatment — Helvetica Neue 200 italic
          with proportional tracking (fontSize / 3, DecoWordmark's navbar
          preset). textAnchor=middle measures glyphs + tracking, and the
          trailing letter-space after the final A pulls the visual centre
          left by half the tracking — the +1.5px x offset cancels it (same
          correction the old pill label needed via padding). */}
      <svg width="44" height="44" viewBox="0 0 48 48" aria-hidden="true" focusable="false">
        <path
          d="M12 8 h24 a8 8 0 0 1 8 8 v10 a8 8 0 0 1 -8 8 H22 l-8 8 v-8 h-2 a8 8 0 0 1 -8 -8 V16 a8 8 0 0 1 8 -8 Z"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinejoin="round"
        />
        <text
          x="25.5"
          y="22"
          textAnchor="middle"
          dominantBaseline="middle"
          fill="currentColor"
          style={{
            fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif",
            fontWeight: 200,
            fontStyle: 'italic',
            // Inherited from the enclosing <button> under the condensed
            // display rules — reset so BETA keeps the wordmark voice.
            fontStretch: 'normal',
            fontSize: '9px',
            letterSpacing: '3px',
          }}
        >
          BETA
        </text>
      </svg>
    </button>
  );

  const panel = open ? ReactDOM.createPortal(
    <div className="modal" onKeyDown={trap.onKeyDown}>
      <div
        className="modal-content"
        style={{ maxWidth: '520px' }}
        role="dialog"
        aria-modal="true"
        aria-labelledby="feedback-panel-title"
        ref={dialogRef}
        tabIndex={-1}
      >
        <div className="modal-header">
          <h2 id="feedback-panel-title">Send Feedback</h2>
          <button className="close-btn" onClick={() => !submitting && setOpen(false)} aria-label="Close">&times;</button>
        </div>

        {sent ? (
          <div role="status">
            <p style={{ marginTop: 0, fontSize: '14px', lineHeight: 1.6 }}>
              Got it — thank you. We’re a small team in beta, so we read and triage every
              report ourselves.
              {userEmail ? ` If we need more detail, we’ll follow up at ${userEmail}.` : ''}
            </p>
            <button type="button" className="btn btn-primary" style={{ width: '100%', minHeight: '44px' }} onClick={() => setOpen(false)}>
              Close
            </button>
          </div>
        ) : (
          <>
            {/* The ONE static intro bubble. Not a conversation — see FeedbackForm. */}
            <div style={{ background: 'var(--light-gray)', borderRadius: '12px 12px 12px 4px', padding: '12px 14px', marginBottom: '16px', fontSize: '14px', lineHeight: 1.55 }}>
              We’re in beta and we read every report. Tell us what happened and we’ll
              look into it.
            </div>

            <FeedbackForm
              fields={fields}
              onChange={change}
              onSubmit={submit}
              submitting={submitting}
              error={error}
              pageLabel={pageLabelFor(currentPath())}
              isMobile={isMobile}
              restoredDraft={restoredDraft}
              onDiscardDraft={discardDraft}
            />

            {copyFallback && (
              <button
                type="button"
                onClick={copyReport}
                style={{ width: '100%', minHeight: '44px', marginTop: '10px', background: 'none', border: '1px solid var(--medium-gray)', borderRadius: '6px', cursor: 'pointer', fontSize: '13px' }}
              >
                Copy my report to the clipboard
              </button>
            )}
          </>
        )}
      </div>
    </div>,
    document.body,
  ) : null;

  return <>{fab}{panel}</>;
};

export default FeedbackWidget;
